import './index.css'

import cn from 'clsx'
import React, { useCallback, useState } from 'react'

import Button from 'components/ui/button'
import Collapsible from 'components/ui/collapsible'
import Heading from 'components/ui/heading'
import Icon, { IconKey } from 'components/ui/icon'

import useLocalStorage from 'hooks/use-local-storage'

const ALERT_COLLAPSED = 'alert_collapsed_'
const ALERT_DISMISSED = 'alert_dismissed_'
type Props = {
  actions?: React.ReactNode
  children: React.ReactNode
  className?: string
  collapsible?: boolean
  dismissible?: boolean
  icon?: IconKey
  id?: string
  status?: 'change' | 'danger' | 'info' | 'warning'
  title?: string
}

// TODO:
// 1. Remove className prop and clean existing overrides
// 2. Add status prop to existing instances
/**
 * @deprecated Use `DiscoCallout` from `components/ds` instead.
 * @see {@link components/ds/DiscoCallout}
 * @migration
 * - Replace `<Alert>` with `<DiscoCallout>` from components/ds
 * - Status mapping (legacy → DS type):
 *   - status="warning" → type="warning"
 *   - status="danger" → type="error"
 *   - status="info" → type="info"
 *   - status="change" → type="accent" or type="feature"
 * - Prop mapping:
 *   - children → children (same)
 *   - dismissible → dismissible (same), use dismissKey for localStorage persistence
 *   - collapsible → Not directly supported; consider custom implementation
 *   - icon → DS DiscoCallout auto-selects icon based on type
 *   - title → Compose with `<Strong>` or `<Heading>` inside children
 *   - actions → Compose action buttons inside children
 *   - id → Use dismissKey for localStorage dismiss persistence
 * - DS DiscoCallout supports: size ("1"|"2"|"3"), variant ("solid"|"soft"), highContrast
 *
 * An alert component with configurable status, collapsible and dismissible behavior
 *
 * @param actions - Action buttons to display in the alert
 * @param children - Content of the alert message
 * @param className - Additional CSS class name (default: '')
 * @param collapsible - Whether the alert can be collapsed
 * @param dismissible - Whether the alert can be dismissed
 * @param icon - Icon to display in the alert
 * @param id - Unique identifier for localStorage persistence (default: '')
 * @param status - Alert status type (default: 'warning')
 * @param title - Title of the alert
 * @returns The alert component or null if dismissed
 */
const Alert = ({
  actions,
  children,
  className = '',
  collapsible,
  dismissible,
  icon,
  id = '',
  status = 'warning',
  title,
}: Props): null | React.ReactElement<'div'> => {
  const [collapsedDate, setCollapsedDate] = useLocalStorage<
    Date | null | undefined
  >(ALERT_COLLAPSED + id, null)
  const [dismissedDate, setDismissedDate] = useLocalStorage<
    Date | null | undefined
  >(ALERT_DISMISSED + id, null)
  const [isCollapsed, setIsCollapsed] = useState<boolean>(
    id ? Boolean(collapsedDate) : false,
  )
  const [isDismissed, setIsDismissed] = useState<boolean>(
    Boolean(dismissedDate),
  )
  const handleDismiss = useCallback(() => {
    setIsDismissed(true)

    if (id) {
      setDismissedDate(new Date())
    }
  }, [id])
  const toggleCollapsible = useCallback(() => {
    setIsCollapsed(isCollapsed => {
      if (id && !isCollapsed) {
        setCollapsedDate(new Date())
      }

      return !isCollapsed
    })
  }, [id])
  if (isDismissed) return null
  const content = (
    <div className="Alert-content">
      {children}
      {actions && <div className="Alert-content-actions">{actions}</div>}
    </div>
  )
  return (
    <div
      className={cn('Alert', className, {
        'Alert--change': status === 'change',
        'Alert--danger': status === 'danger',
        'Alert--info': status === 'info',
        'Alert--warning': status === 'warning',
      })}
    >
      {icon && (
        <div className="Alert-icon">
          <Icon type={icon} />
        </div>
      )}

      <div className="Alert-container">
        {title && (
          <div className="Alert-title">
            <Heading size={500}>{title}</Heading>
          </div>
        )}

        {collapsible && title ? (
          <Collapsible open={!isCollapsed}>{content}</Collapsible>
        ) : (
          content
        )}
      </div>

      {collapsible && title && (
        <div className="Alert-button">
          <div
            className={cn('Alert-collapsible-button', {
              'Alert-collapsible-button--open': !isCollapsed,
            })}
            onClick={toggleCollapsible}
          >
            <Icon type="arrow-right" />
          </div>
        </div>
      )}

      {!collapsible && dismissible && (
        <div className="Alert-button">
          <Button
            appearance="icon"
            compact
            icon="close"
            onClick={handleDismiss}
          />
        </div>
      )}
    </div>
  )
}

export default Alert
